import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

// ========== 预设主题（背景图片）==========
export enum Wallpaper {
  /** 锋影 */
  STEALTH = 'stealth',
  /** 幻光 */
  GLEAM = 'gleam',
  /** 舒卷 */
  SCROLL = 'scroll',
  /** 素彩 */
  PASTEL = 'pastel',
  /** 隐光 */
  SUBTLE = 'subtle',
  /** 末站 */
  TERMINUS = 'terminus',
  /** 骇墟 */
  HACKRUIN = 'hackruin',
  /** 月舷 */
  MOONVIEW = 'moonview',
  /** 闪痕 */
  PINK = 'pink',
  /** 莹丘 */
  MOUND = 'mound',
  /** 绒屿 */
  VELVET_ISLE = 'velvet-isle',
  /** 云隐 */
  CLOUD_NOOK = 'cloud-nook',
  /** 晴笺 */
  SUNNY = 'sunny',
  /** 柔台 */
  GENTLE = 'gentle',
}

export const WALLPAPER_STORAGE_KEY = 'rapoo-wallpaper'
export const DEFAULT_THEME_WALLPAPER: Record<'light' | 'dark', Wallpaper> = {
  light: Wallpaper.GLEAM,
  dark: Wallpaper.STEALTH,
}

export const getDefaultWallpaperByTheme = (isDark: boolean) =>
  isDark ? DEFAULT_THEME_WALLPAPER.dark : DEFAULT_THEME_WALLPAPER.light

// ========== 自定义主题 ==========
export type CustomWallpaperType = 'solid' | 'gradient' | 'component'

export interface CustomWallpaperConfig {
  type: CustomWallpaperType
  /** 纯色背景色值 */
  color?: string
  /** 渐变 CSS 值，如 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' */
  gradient?: string
  /** 组件背景的组件名称 */
  component?: string
}

// 预设的自定义主题
export enum CustomWallpaper {
  /** 折射 */
  REFRACT = 'refract',
  /** 霓澜 */
  NEON_BLUE = 'neon-blue',
}

// 自定义主题配置表
export const CUSTOM_WALLPAPER_CONFIGS: Record<string, CustomWallpaperConfig> = {
  [CustomWallpaper.REFRACT]: {
    type: 'component',
    component: 'ColorBends',
  },
  [CustomWallpaper.NEON_BLUE]: {
    type: 'component',
    component: 'Grainient',
  },
}

// ========== 壁纸类型判断 ==========
export type WallpaperCategory = 'preset' | 'systemCustom' | 'userCustom'

export interface UserCustomWallpaperInfo {
  createdAt: number
  id: string
  mimeType: string
  theme: 'light' | 'dark' | 'system'
  size: number
  order: number
  type: 'userCustom'
  url: string
}

export type WallpaperSelectOption =
  | {
      category: 'preset'
      id: string
    }
  | {
      category: 'systemCustom'
      id: string
      config: CustomWallpaperConfig
    }
  | {
      category: 'userCustom'
      wallpaper: UserCustomWallpaperInfo
    }

// ========== 页面级临时覆盖 目前只支持组件类型背景 ==========
export interface PageOverrideConfig {
  /** 组件名（需在 BackgroundComponents 中注册） */
  component: string
  /** 传递给背景组件的 props */
  props?: Record<string, unknown>
}

export const useWallpaperStore = defineStore(
  'wallpaper',
  () => {
    // 当前壁纸 ID（预设或自定义）
    const currentWallpaper = ref<string>(Wallpaper.STEALTH)

    // 当前壁纸类别
    const currentCategory = ref<WallpaperCategory>('preset')

    // 当前自定义壁纸配置（仅当 category 为 custom 时有效）
    const customConfig = ref<CustomWallpaperConfig | null>(null)

    // 当前用户上传壁纸信息（仅当 category 为 userCustom 时有效）
    const userCustomWallpaper = ref<UserCustomWallpaperInfo | null>(null)

    // 页面级临时覆盖（不持久化，页面离开时清除）
    const pageOverride = ref<PageOverrideConfig | null>(null)

    // 是否为预设壁纸
    const isPreset = computed(() => currentCategory.value === 'preset')

    // 是否为系统自定义壁纸
    const isSystemCustom = computed(() => currentCategory.value === 'systemCustom')

    // 是否为用户上传壁纸
    const isUserCustom = computed(() => currentCategory.value === 'userCustom')

    // 是否为组件类型背景
    const isComponentBg = computed(
      () =>
        pageOverride.value !== null ||
        (isSystemCustom.value && customConfig.value?.type === 'component'),
    )

    // 是否有页面级覆盖
    const hasPageOverride = computed(() => pageOverride.value !== null)

    const selectWallpaper = (option: WallpaperSelectOption) => {
      if (option.category === 'preset') {
        currentWallpaper.value = option.id
        currentCategory.value = 'preset'
        customConfig.value = null
        userCustomWallpaper.value = null
        return
      }

      if (option.category === 'systemCustom') {
        currentWallpaper.value = option.id
        currentCategory.value = 'systemCustom'
        customConfig.value = option.config
        userCustomWallpaper.value = null
        return
      }

      currentWallpaper.value = option.wallpaper.id
      currentCategory.value = 'userCustom'
      customConfig.value = null
      userCustomWallpaper.value = option.wallpaper
    }

    // 设置预设壁纸
    const setWallpaper = (img: string) => {
      selectWallpaper({
        category: 'preset',
        id: img,
      })
    }

    // 设置自定义壁纸
    const setCustomWallpaper = (id: string, config: CustomWallpaperConfig) => {
      selectWallpaper({
        category: 'systemCustom',
        id,
        config,
      })
    }

    // 设置用户上传壁纸
    const setUserCustomWallpaper = (wallpaper: UserCustomWallpaperInfo) => {
      selectWallpaper({
        category: 'userCustom',
        wallpaper,
      })
    }

    // 设置预注册的自定义壁纸
    const setRegisteredCustomWallpaper = (id: string) => {
      const config = CUSTOM_WALLPAPER_CONFIGS[id]
      if (config) {
        setCustomWallpaper(id, config)
      }
    }

    // 设置页面级临时覆盖（进入特定页面时调用）
    const setPageOverride = (config: PageOverrideConfig) => {
      pageOverride.value = config
    }

    const hasPersistedWallpaper = () => window.localStorage.getItem(WALLPAPER_STORAGE_KEY) !== null

    const isValidCurrentWallpaper = () => {
      if (currentCategory.value === 'preset') {
        return Object.values(Wallpaper).includes(currentWallpaper.value as Wallpaper)
      }

      if (currentCategory.value === 'systemCustom') {
        return Boolean(CUSTOM_WALLPAPER_CONFIGS[currentWallpaper.value])
      }

      return userCustomWallpaper.value?.id === currentWallpaper.value
    }

    const initDefaultWallpaper = (isDark: boolean) => {
      if (hasPersistedWallpaper() && isValidCurrentWallpaper()) return

      selectWallpaper({
        category: 'preset',
        id: getDefaultWallpaperByTheme(isDark),
      })
    }

    // 清除页面级临时覆盖（离开页面时调用）
    const clearPageOverride = () => {
      pageOverride.value = null
    }

    return {
      currentWallpaper,
      currentCategory,
      customConfig,
      userCustomWallpaper,
      pageOverride,
      isPreset,
      isSystemCustom,
      isUserCustom,
      isComponentBg,
      hasPageOverride,
      selectWallpaper,
      setWallpaper,
      setCustomWallpaper,
      setUserCustomWallpaper,
      setRegisteredCustomWallpaper,
      initDefaultWallpaper,
      setPageOverride,
      clearPageOverride,
    }
  },
  {
    persist: {
      key: WALLPAPER_STORAGE_KEY,
      pick: ['currentWallpaper', 'currentCategory', 'customConfig', 'userCustomWallpaper'],
    },
  },
)
